import { useMemo, useState } from 'react';
import { Flex, HStack, Stack, Text, VStack } from '@chakra-ui/layout';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Spinner,
  Switch,
  useToast,
} from '@chakra-ui/react';
import { Trans, t } from '@lingui/macro';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { withPermission } from '../withPermission';
import {
  PrePlacementDocumentTypeSchema,
  type PrePlacementDocumentType,
} from '@duke-hq/svc-api-db/schemas/enums/PrePlacementDocumentType.schema';

type DocumentTypeMeta = {
  label: string;
  description: string;
};

const ALL_PRE_PLACEMENT_DOCUMENT_TYPES = [
  PrePlacementDocumentTypeSchema.enum.WORKING_WITH_CHILDREN_CHECK,
  PrePlacementDocumentTypeSchema.enum.POLICE_CHECK,
  PrePlacementDocumentTypeSchema.enum.RESUME,
  PrePlacementDocumentTypeSchema.enum.IMMUNISATION_RECORD,
  PrePlacementDocumentTypeSchema.enum.WORKING_WITH_VULNERABLE_PEOPLE_CHECK,
  PrePlacementDocumentTypeSchema.enum.NDIS_WORKER_SCREENING_CHECK,
] satisfies PrePlacementDocumentType[];

export const WorkPlacementRequirementsPanel = withPermission(
  'canManageWorkPlacements',
  function WorkPlacementRequirementsPanel({ courseId }: { courseId: string }) {
    const toast = useToast();
    const [pendingType, setPendingType] = useState<PrePlacementDocumentType | null>(null);
    const [isBulkPending, setIsBulkPending] = useState(false);

    const {
      data: requirements,
      isLoading,
      refetch,
    } = trpcAdmin.workPlacements.courseRequirements.list.useQuery({ courseId });

    const addRequirement = trpcAdmin.workPlacements.courseRequirements.add.useMutation();
    const removeRequirement = trpcAdmin.workPlacements.courseRequirements.remove.useMutation();

    const documentTypeMeta = useMemo<Record<PrePlacementDocumentType, DocumentTypeMeta>>(
      () => ({
        [PrePlacementDocumentTypeSchema.enum.WORKING_WITH_CHILDREN_CHECK]: {
          label: t`Working with Children Check`,
          description: t`A government-issued clearance verifying the holder has no history of offences against children. Required for placements involving contact with minors.`,
        },
        [PrePlacementDocumentTypeSchema.enum.POLICE_CHECK]: {
          label: t`Police Check`,
          description: t`A National Police Certificate confirming the student has no relevant criminal history. Standard requirement for most professional placements.`,
        },
        [PrePlacementDocumentTypeSchema.enum.RESUME]: {
          label: t`Resume`,
          description: t`An up-to-date CV outlining the student's work history, education, and relevant skills for placement suitability assessment.`,
        },
        [PrePlacementDocumentTypeSchema.enum.IMMUNISATION_RECORD]: {
          label: t`Immunisation Record`,
          description: t`Documentation of current vaccinations as required by the placement host. Typically includes flu, hepatitis B, and other relevant immunisations.`,
        },
        [PrePlacementDocumentTypeSchema.enum.WORKING_WITH_VULNERABLE_PEOPLE_CHECK]: {
          label: t`Working with Vulnerable People Check`,
          description: t`A government-issued clearance required for roles involving direct contact with vulnerable adults, including those with disabilities or the elderly.`,
        },
        [PrePlacementDocumentTypeSchema.enum.NDIS_WORKER_SCREENING_CHECK]: {
          label: t`NDIS Worker Screening Check`,
          description: t`A mandatory NDIS Worker Screening Check clearance for roles involving direct support of NDIS participants.`,
        },
      }),
      [],
    );

    if (isLoading) {
      return (
        <VStack p={4}>
          <Spinner />
        </VStack>
      );
    }

    const enabledTypes = new Set(requirements?.map((r) => r.documentType));
    const requirementById = new Map(requirements?.map((r) => [r.documentType, r.id]));

    const isAllEnabled = enabledTypes.size === ALL_PRE_PLACEMENT_DOCUMENT_TYPES.length;
    const isNoneEnabled = enabledTypes.size === 0;

    const runBulk = async (ops: Promise<unknown>[], successTitle: string) => {
      setIsBulkPending(true);
      try {
        const results = await Promise.allSettled(ops);
        await refetch();
        const failed = results.filter((r) => r.status === 'rejected');
        if (failed.length > 0) {
          toast({
            title: t`${failed.length} requirement(s) failed to update.`,
            status: 'error',
            isClosable: true,
          });
        } else {
          toast({ title: successTitle, status: 'success', isClosable: true });
        }
      } finally {
        setIsBulkPending(false);
      }
    };

    const handleSelectAll = () => {
      const toAdd = ALL_PRE_PLACEMENT_DOCUMENT_TYPES.filter((type) => !enabledTypes.has(type));
      void runBulk(
        toAdd.map((documentType) => addRequirement.mutateAsync({ courseId, documentType })),
        t`All requirements added.`,
      );
    };

    const handleRemoveAll = () => {
      void runBulk(
        [...requirementById.values()].map((id) => removeRequirement.mutateAsync({ id })),
        t`All requirements removed.`,
      );
    };

    const handleToggle = async (documentType: PrePlacementDocumentType) => {
      const isEnabled = enabledTypes.has(documentType);
      setPendingType(documentType);
      try {
        if (isEnabled) {
          const id = requirementById.get(documentType);
          if (id != null) {
            await removeRequirement.mutateAsync({ id });
            toast({ title: t`Requirement removed.`, status: 'success', isClosable: true });
          }
        } else {
          await addRequirement.mutateAsync({ courseId, documentType });
          toast({ title: t`Requirement added.`, status: 'success', isClosable: true });
        }
        await refetch();
      } catch (error) {
        toast({
          title: error instanceof Error ? error.message : t`Something went wrong.`,
          status: 'error',
          isClosable: true,
        });
      } finally {
        setPendingType(null);
      }
    };

    return (
      <VStack p={4} gap={4}>
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            <Trans>
              Configure which pre-placement documents students must submit before beginning a work
              placement for this course.
            </Trans>
          </AlertDescription>
        </Alert>
        <HStack w="100%" justify="flex-end">
          <Button
            variant="link"
            size="sm"
            colorScheme="brand"
            isDisabled={isAllEnabled || isBulkPending}
            isLoading={isBulkPending && !isAllEnabled}
            onClick={handleSelectAll}
          >
            <Trans>Select all</Trans>
          </Button>
          <Text fontSize="sm" color="fg.muted">
            ·
          </Text>
          <Button
            variant="link"
            size="sm"
            colorScheme="red"
            isDisabled={isNoneEnabled || isBulkPending}
            isLoading={isBulkPending && !isNoneEnabled}
            onClick={handleRemoveAll}
          >
            <Trans>Remove all</Trans>
          </Button>
        </HStack>
        <Stack borderWidth="1px" borderColor="border.emphasis" w="100%" rounded="md" gap={0}>
          {ALL_PRE_PLACEMENT_DOCUMENT_TYPES.map((documentType) => {
            const meta = documentTypeMeta[documentType];
            const isEnabled = enabledTypes.has(documentType);
            const isPending = pendingType === documentType;

            return (
              <Flex
                key={documentType}
                _notLast={{
                  borderBottomWidth: 1,
                  borderBottomColor: 'border.emphasis',
                }}
                px={4}
                py={3}
                justifyContent="space-between"
                alignItems="center"
                gap={4}
              >
                <Flex direction="column">
                  <Text fontWeight="medium">{meta.label}</Text>
                  <Text fontSize="xs" color="fg.muted">
                    {meta.description}
                  </Text>
                </Flex>
                {isPending ? (
                  <Spinner size="sm" flexShrink={0} />
                ) : (
                  <Switch
                    colorScheme="brand"
                    isChecked={isEnabled}
                    isDisabled={isBulkPending}
                    flexShrink={0}
                    aria-label={
                      isEnabled
                        ? t`Remove ${meta.label} requirement`
                        : t`Add ${meta.label} requirement`
                    }
                    onChange={() => void handleToggle(documentType)}
                  />
                )}
              </Flex>
            );
          })}
        </Stack>
        <Text fontSize="sm" color="fg.muted" alignSelf="flex-end">
          {enabledTypes.size}/{ALL_PRE_PLACEMENT_DOCUMENT_TYPES.length} <Trans>required</Trans>
        </Text>
      </VStack>
    );
  },
);
